import { logger } from '@/core/logger';
import { navigateToWelcomeAfterLogout } from '@/core/navigation/logoutNavigation';
import { trackDynatraceEvent } from '@/core/services/dynatraceService';
import { useAppStore } from '@/core/stores/appStore';
import { getClientPlatform } from '@/core/utils/clientPlatform';
import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';

// Extend AxiosRequestConfig to include metadata
declare module 'axios' {
    interface AxiosRequestConfig {
        metadata?: {
            startTime?: number;
        };
    }
}

// Require EXPO_PUBLIC_API_URL.
const API_BASE_URL = process.env.EXPO_PUBLIC_API_URL as string;
if (!API_BASE_URL) {
    throw new Error('EXPO_PUBLIC_API_URL is not set. Please configure it in app config/env.');
}

// Request deduplication cache - prevents duplicate identical requests
interface PendingRequest {
    promise: Promise<any>;
    timestamp: number;
}

function isUaePassAuthRequest(error: AxiosError): boolean {
    return Boolean(error.config?.url?.includes('/v1/uae-pass/'));
}

/**
 * Guest catalogue (`GET /v1/projects`, Basic Auth). Failures must not
 * clear a logged-in Bearer session or force the session-expired error screen.
 */
function isGuestProjectsRequest(error: AxiosError): boolean {
    return Boolean(error.config?.url?.includes('/v1/projects'));
}

const READ_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

const PASSCODE_AUTH_PATHS = [
    '/set-passcode',
    '/verify-passcode',
    '/skip-passcode',
    '/change-passcode',
    '/forgot-passcode',
] as const;

type RequestTokenType = 'passcode' | 'login' | 'none';

interface RequestAuthTokens {
    readonly token: string | null;
    readonly passcodeToken: string | null;
}

interface ResolvedRequestAuth {
    readonly token: string | null;
    readonly tokenType: RequestTokenType;
    readonly isPasscodeEndpoint: boolean;
    readonly usedLoginFallback: boolean;
}

function isPasscodeAuthEndpoint(url: string): boolean {
    return PASSCODE_AUTH_PATHS.some((path) => url.includes(path));
}

function resolveRequestAuth(url: string, auth: RequestAuthTokens): ResolvedRequestAuth {
    if (isPasscodeAuthEndpoint(url)) {
        return {
            token: auth.token,
            tokenType: auth.token ? 'login' : 'none',
            isPasscodeEndpoint: true,
            usedLoginFallback: false,
        };
    }
    const token = auth.passcodeToken || auth.token;
    const usedLoginFallback = Boolean(token && !auth.passcodeToken && auth.token);
    let tokenType: RequestTokenType = 'none';
    if (token) {
        tokenType = auth.passcodeToken === token ? 'passcode' : 'login';
    }
    return { token, tokenType, isPasscodeEndpoint: false, usedLoginFallback };
}

function readAuthorizationHeader(config: AxiosRequestConfig): string | undefined {
    const headers = config.headers;
    if (!headers) {
        return undefined;
    }
    const headerReader = headers as { get?: (key: string) => unknown };
    if (typeof headerReader.get === 'function') {
        const value = headerReader.get('Authorization');
        return typeof value === 'string' ? value : undefined;
    }
    const record = headers as Record<string, unknown>;
    const value = record.Authorization ?? record.authorization;
    return typeof value === 'string' ? value : undefined;
}

/** Fetches and polls. These run without a button press. */
const BACKGROUND_API_PATHS = [
    '/v1/payment/fees',
    '/v1/payment/status',
];

function isBackgroundApiCall(config: AxiosRequestConfig | undefined): boolean {
    const method = (config?.method ?? 'GET').toUpperCase();
    if (READ_METHODS.has(method)) {
        return true;
    }
    const url = config?.url ?? '';
    return BACKGROUND_API_PATHS.some((path) => url.includes(path));
}

function readApiErrorMessage(error: AxiosError): string {
    const data = error.response?.data;
    if (typeof data === 'string' && data.trim().length > 0) {
        return data;
    }
    if (typeof data === 'object' && data !== null) {
        const body = data as { message?: unknown; error?: unknown };
        if (typeof body.message === 'string' && body.message.trim().length > 0) {
            return body.message;
        }
        if (typeof body.error === 'string' && body.error.trim().length > 0) {
            return body.error;
        }
    }
    return error.message;
}

/** User-started writes only. Reads, fee lookup, and status polling stay quiet. */
function reportActionApiResult(
    outcome: 'success' | 'failure',
    config: AxiosRequestConfig | undefined,
    status: number | string | undefined,
    failedMessage?: string,
): void {
    if (isBackgroundApiCall(config)) {
        return;
    }
    try {
        trackDynatraceEvent({
            p_screen: 'api',
            p_actionName: outcome === 'success' ? 'API Success' : 'API Error',
            eventType: outcome === 'success' ? 'action' : 'error',
            apiUrl: config?.url || '',
            apiMethod: config?.method?.toUpperCase() || 'UNKNOWN',
            httpStatus: status ?? 'NETWORK_ERROR',
            ...(failedMessage ? { p_failedMessage: failedMessage } : {}),
        });
    } catch (dynatraceError) {
        logger.warn('API:Failed to report action result to Dynatrace', dynatraceError);
    }
}

// Enhanced REST client with better error handling and logging
export class APIClient {
    private instance: AxiosInstance;
    private readonly enableLogging: boolean;
    private readonly logBodies: boolean;
    private readonly pendingRequests: Map<string, PendingRequest> = new Map();
    private readonly DEDUP_WINDOW_MS = 5000; // Deduplicate requests within 5 seconds (increased for slow APIs)

    constructor(baseURL: string) {
        this.instance = axios.create({
            baseURL,
            timeout: 60000, // Increased timeout
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                platform: getClientPlatform(),
            },
        });

        // Enhanced logging config
        // If env variable is set, use it; otherwise: true in dev, false in release
        this.enableLogging = process.env.EXPO_PUBLIC_ENABLE_API_LOGS !== undefined
            ? process.env.EXPO_PUBLIC_ENABLE_API_LOGS === 'true'
            : __DEV__;
        this.logBodies = process.env.EXPO_PUBLIC_LOG_API_BODIES !== undefined
            ? process.env.EXPO_PUBLIC_LOG_API_BODIES === 'true'
            : __DEV__;

        // Setup interceptors
        this.setupInterceptors();

        if (this.enableLogging) {
            logger.info('API:Client initialized', { baseURL, logging: this.enableLogging });
        }
    }

    /**
     * Generate a unique key for request deduplication
     */
    private getRequestKey(config: AxiosRequestConfig): string {
        const method = config.method?.toUpperCase() || 'GET';
        const url = config.url || '';
        const params = config.params ? JSON.stringify(config.params) : '';
        const data = config.data ? JSON.stringify(config.data) : '';
        return `${method}:${url}:${params}:${data}`;
    }

    /**
     * Clean up stale pending requests
     */
    private cleanupPendingRequests(): void {
        const now = Date.now();
        for (const [key, request] of this.pendingRequests.entries()) {
            if (now - request.timestamp > this.DEDUP_WINDOW_MS * 2) {
                this.pendingRequests.delete(key);
            }
        }
    }

    private setupInterceptors(): void {
        // Request interceptor
        this.instance.interceptors.request.use(
            (config) => {
                // Clean up stale requests periodically
                this.cleanupPendingRequests();

                // Add request metadata for timing
                config.metadata = { startTime: Date.now() };

                // Inject auth token if available
                this.injectAuthToken(config);
                this.injectPlatformHeader(config);

                // Log request
                this.logRequest(config);

                return config;
            },
            (error) => {
                logger.error('API:Request setup failed', error);
                return Promise.reject(error);
            }
        );

        // Response interceptor
        this.instance.interceptors.response.use(
            (response) => {
                reportActionApiResult('success', response.config, response.status);
                this.logResponse(response);
                return response;
            },
            async (error) => {
                this.logError(error);

                // Handle 400 "Device ID mismatch" - another device has logged in, logout user
                if (error.response?.status === 400) {
                    const errorMessage = error.response?.data?.message || '';
                    if (errorMessage.toLowerCase().includes('device id mismatch')) {
                        logger.warn('API:400 Device ID mismatch - another device logged in, clearing auth session');
                        try {
                            // Set error message for dialog
                            const { useAppStore } = await import('@/core/stores/appStore');
                            useAppStore.getState().setUnauthorizedError('auth.deviceIdMismatch');

                            // Clear authentication state via central store (single source of truth)
                            const clearAuth = useAppStore.getState().clearAuth;
                            await clearAuth();

                            // Preserve full path including query params for redirect after login
                            const pathname =
                                typeof window !== 'undefined' ? window.location?.pathname || '/' : '/';
                            const search =
                                typeof window !== 'undefined' ? window.location?.search || '' : '';
                            const currentPath = `${pathname}${search}`;

                            // Generic navigation after logout:
                            // clears stack and sends user to welcome/login entry
                            await navigateToWelcomeAfterLogout({ redirectPath: currentPath });
                        } catch (navError) {
                            logger.error('API:Failed to handle device ID mismatch logout', navError);
                            // Fallback navigation (still clear stack and go to welcome/login entry)
                            await navigateToWelcomeAfterLogout();
                        }
                    }
                }

                // UAE PASS login failures are authentication-entry errors, not
                // expired sessions. Let the UAE PASS mapper handle their codes.
                // Handle 401/403 Unauthorized responses (token expired or invalid)
                if (
                    (error.response?.status === 401 ||
                        error.response?.status === 403) &&
                    !isUaePassAuthRequest(error) &&
                    !isGuestProjectsRequest(error)
                ) {
                    logger.warn(`API:${error.response.status} Unauthorized - clearing auth session`);
                    try {
                        // Set error message for dialog - always use translation key for consistent messaging
                        const { useAppStore } = await import('@/core/stores/appStore');
                        // Always use the translation key for session expired, regardless of server message
                        useAppStore.getState().setUnauthorizedError('auth.sessionExpired');

                        // Clear authentication state via central store (single source of truth)
                        const clearAuth = useAppStore.getState().clearAuth;
                        await clearAuth();

                        // Preserve full path including query params for redirect after login
                        const pathname =
                            typeof window !== 'undefined' ? window.location?.pathname || '/' : '/';
                        const search =
                            typeof window !== 'undefined' ? window.location?.search || '' : '';
                        const currentPath = `${pathname}${search}`;

                        // Generic navigation after logout/session-expire:
                        // clears stack and sends user to welcome/login entry
                        await navigateToWelcomeAfterLogout({ redirectPath: currentPath });
                    } catch (navError) {
                        logger.error('API:Failed to handle auth error logout', navError);
                        // Fallback navigation (still clear stack and go to welcome/login entry)
                        await navigateToWelcomeAfterLogout();
                    }
                }

                return Promise.reject(this.normalizeError(error));
            }
        );
    }

    private injectPlatformHeader(config: AxiosRequestConfig): void {
        const platform = getClientPlatform();
        const headers = config.headers as
            | { set?: (key: string, value: string) => void }
            | Record<string, string>
            | undefined;
        if (headers && typeof headers.set === 'function') {
            headers.set('platform', platform);
            return;
        }
        config.headers = {
            ...(headers as Record<string, string> | undefined),
            platform,
        };
    }

    private injectAuthToken(config: AxiosRequestConfig): void {
        try {
            const authState = useAppStore.getState().auth;
            const url = config.url || '';
            // Passcode endpoints use the login token. Other calls prefer the
            // passcode token and fall back to the login token.
            const resolved = resolveRequestAuth(url, authState);
            if (resolved.isPasscodeEndpoint) {
                logger.info('API:Using login token for passcode endpoint', { url });
            } else if (resolved.usedLoginFallback) {
                logger.warn('API:Using login token as fallback (passcode token not available)', { url });
            }

            // Log which token path was used without exposing full secrets
            logger.info('API:Injecting auth token', {
                token: resolved.token ? 'present' : 'null',
                tokenType: resolved.tokenType,
                isPasscodeEndpoint: resolved.isPasscodeEndpoint,
                url,
                tokenPreview: resolved.token ? `${resolved.token.slice(0, 6)}…` : null,
            });

            // Preserve ADHA Basic Auth (and any explicit Authorization) — do not
            // overwrite with Bearer. AxiosHeaders may not expose `.Authorization`.
            const existingAuth = readAuthorizationHeader(config);
            if (resolved.token && !existingAuth) {
                config.headers = config.headers ?? {};
                (config.headers as Record<string, string>).Authorization =
                    `Bearer ${resolved.token}`;
            }
        } catch (error) {
            logger.warn('API:Failed to inject auth token', error);
        }
    }

    private logRequest(config: AxiosRequestConfig): void {
        if (!this.enableLogging) return;

        const { method, url, headers, params, data } = config;
        const logData: any = {
            method: method?.toUpperCase(),
            url,
            headers: this.sanitizeHeaders(headers),
            params,
        };

        if (this.logBodies && data) {
            logData.body = data;
        }

        logger.info('API:Request', logData);
    }

    private logResponse(response: AxiosResponse): void {
        if (!this.enableLogging) return;

        const startTime = response.config.metadata?.startTime;
        const duration = startTime ? Date.now() - startTime : undefined;

        const { status, statusText, config, headers, data } = response;
        const logData: any = {
            url: config.url,
            status,
            statusText,
            headers: this.sanitizeHeaders(headers),
            duration: `${duration}ms`,
        };

        if (this.logBodies && data) {
            // Create a deep copy to ensure nested objects are properly serialized
            // The logger will handle formatting nested objects for full visibility
            if (typeof data === 'object' && data !== null) {
                try {
                    // Deep copy to ensure all nested objects are plain objects (not class instances)
                    logData.body = JSON.parse(JSON.stringify(data));
                } catch {
                    // Fallback to original data if stringify fails
                    logData.body = data;
                }
            } else {
                logData.body = data;
            }
        }

        logger.info('API:Response', logData);
    }

    private logError(error: AxiosError): void {
        const actionStatus = error.response?.status;
        const isActionNetworkError = !error.response && Boolean(error.message);
        const shouldReportAction =
            (actionStatus != null && actionStatus !== 401 && actionStatus !== 403) ||
            isActionNetworkError;
        if (shouldReportAction) {
            reportActionApiResult(
                'failure',
                error.config,
                actionStatus,
                readApiErrorMessage(error),
            );
        }

        if (!this.enableLogging) return;

        const config = error.config;
        const response = error.response;
        const startTime = config?.metadata?.startTime;
        const duration = startTime ? Date.now() - startTime : undefined;

        const logData: any = {
            url: config?.url,
            method: config?.method?.toUpperCase(),
            message: error.message,
            duration: duration ? `${duration}ms` : undefined,
        };

        if (response) {
            logData.status = response.status;
            logData.statusText = response.statusText;
            if (this.logBodies && response.data) {
                // Create a deep copy to ensure nested objects are properly serialized
                // The logger will handle formatting nested objects for full visibility
                if (typeof response.data === 'object' && response.data !== null) {
                    try {
                        // Deep copy to ensure all nested objects are plain objects (not class instances)
                        logData.responseBody = JSON.parse(JSON.stringify(response.data));
                    } catch {
                        // Fallback to original data if stringify fails
                        logData.responseBody = response.data;
                    }
                } else {
                    logData.responseBody = response.data;
                }
            }
        }

        logger.warn('API:Error', logData);
    }

    private sanitizeHeaders(headers: any): any {
        if (!headers) return headers;

        const sanitized = { ...headers };
        // Remove sensitive headers from logs
        delete sanitized.Authorization;
        delete sanitized.authorization;
        return sanitized;
    }

    private normalizeError(error: AxiosError): Error {
        // Create a normalized error with useful information
        const responseData = error.response?.data as any;
        const message = responseData?.message ||
            responseData?.error ||
            error.message ||
            'Unknown API error';

        const normalizedError = new Error(message);
        (normalizedError as any).status = error.response?.status;
        (normalizedError as any).response = error.response?.data;
        (normalizedError as any).originalError = error;

        return normalizedError;
    }

    // HTTP Methods with enhanced error handling
    public async get<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<T> {
        // Deduplicate GET requests
        const requestKey = this.getRequestKey({ ...config, method: 'GET', url });
        const pending = this.pendingRequests.get(requestKey);

        if (pending) {
            const age = Date.now() - pending.timestamp;
            if (age < this.DEDUP_WINDOW_MS) {
                // Return existing promise for duplicate request
                logger.debug('API:Deduplicating GET request', { url, age: `${age}ms` });
                return pending.promise;
            } else {
                // Request is stale, remove it
                logger.debug('API:Removing stale pending request', { url, age: `${age}ms` });
                this.pendingRequests.delete(requestKey);
            }
        }

        // Create new request promise
        const requestPromise = (async () => {
            try {
                const res: AxiosResponse<T> = await this.instance.get(url, config);
                return res.data;
            } catch (error) {
                throw this.handleRequestError(error, 'GET', url);
            } finally {
                // Remove from pending after a delay
                setTimeout(() => {
                    this.pendingRequests.delete(requestKey);
                }, this.DEDUP_WINDOW_MS);
            }
        })();

        // Store the promise
        this.pendingRequests.set(requestKey, {
            promise: requestPromise,
            timestamp: Date.now(),
        });

        return requestPromise;
    }

    public async post<T = unknown, B = unknown>(url: string, body?: B, config?: AxiosRequestConfig): Promise<T> {
        try {
            const res: AxiosResponse<T> = await this.instance.post(url, body, config);
            return res.data;
        } catch (error) {
            throw this.handleRequestError(error, 'POST', url);
        }
    }

    public async put<T = unknown, B = unknown>(url: string, body?: B, config?: AxiosRequestConfig): Promise<T> {
        try {
            const res: AxiosResponse<T> = await this.instance.put(url, body, config);
            return res.data;
        } catch (error) {
            throw this.handleRequestError(error, 'PUT', url);
        }
    }

    public async patch<T = unknown, B = unknown>(url: string, body?: B, config?: AxiosRequestConfig): Promise<T> {
        try {
            const res: AxiosResponse<T> = await this.instance.patch(url, body, config);
            return res.data;
        } catch (error) {
            throw this.handleRequestError(error, 'PATCH', url);
        }
    }

    public async delete<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<T> {
        try {
            const res: AxiosResponse<T> = await this.instance.delete(url, config);
            return res.data;
        } catch (error) {
            throw this.handleRequestError(error, 'DELETE', url);
        }
    }

    // Utility methods
    public async upload<T = unknown>(url: string, formData: FormData, config?: AxiosRequestConfig): Promise<T> {
        try {
            const res: AxiosResponse<T> = await this.instance.post(url, formData, {
                ...config,
                headers: {
                    'Content-Type': 'multipart/form-data',
                    ...config?.headers,
                },
            });
            return res.data;
        } catch (error) {
            throw this.handleRequestError(error, 'UPLOAD', url);
        }
    }

    // Access to underlying axios instance for advanced cases
    public get axios(): AxiosInstance {
        return this.instance;
    }

    // Get base URL
    public get baseURL(): string {
        return this.instance.defaults.baseURL || '';
    }

    private handleRequestError(error: any, method: string, url: string): Error {
        // Error is already normalized by the response interceptor
        if (this.enableLogging) {
            logger.error('API:Request failed', {
                method,
                url,
                error: error.message,
                status: error.status,
            });
        }
        return error;
    }
}

export const apiClient = new APIClient(API_BASE_URL);

export type { AxiosRequestConfig };

